import React from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";

// Simple Pie Chart Component
export const SimplePieChart = ({
  data,
  width = 300,
  height = 250,
  colors = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"],
  innerRadius = 0,
  outerRadius = 80,
}) => {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: "100%", height, display: "flex", justifyContent: "center" }}
    >
      <PieChart width={width} height={height}>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          fill="#8884d8"
          paddingAngle={5}
          dataKey="value"
          label={({ name, percent }) =>
            `${name}: ${(percent * 100).toFixed(0)}%`
          }
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.color || colors[index % colors.length]}
            />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </Box>
  );
};

// Simple Bar Chart Component
export const SimpleBarChart = ({
  data,
  width = 500,
  height = 300,
  dataKey = "value",
  nameKey = "name",
  fill = "#8884d8",
  margin = { top: 5, right: 30, left: 20, bottom: 5 },
}) => {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: "100%", height, display: "flex", justifyContent: "center" }}
    >
      <BarChart width={width} height={height} data={data} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={nameKey} />
        <YAxis />
        <Tooltip />
        <Legend />
        <Bar dataKey={dataKey} fill={fill} />
      </BarChart>
    </Box>
  );
};

// Simple Line Chart Component
export const SimpleLineChart = ({
  data,
  width = 500,
  height = 300,
  dataKey = "value",
  nameKey = "name",
  stroke = "#8884d8",
  margin = { top: 5, right: 30, left: 20, bottom: 5 },
}) => {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: "100%", height, display: "flex", justifyContent: "center" }}
    >
      <LineChart width={width} height={height} data={data} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={nameKey} />
        <YAxis />
        <Tooltip />
        <Legend />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={stroke}
          activeDot={{ r: 8 }}
        />
      </LineChart>
    </Box>
  );
};

// Simple Area Chart Component
export const SimpleAreaChart = ({
  data,
  width = 500,
  height = 300,
  dataKey = "value",
  nameKey = "name",
  stroke = "#8884d8",
  fill = "#8884d8",
  margin = { top: 5, right: 30, left: 20, bottom: 5 },
}) => {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  // Log the data to help with debugging
  console.log("SimpleAreaChart data:", data);

  return (
    <Box
      sx={{ width: "100%", height, display: "flex", justifyContent: "center" }}
    >
      <AreaChart width={width} height={height} data={data} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={nameKey} />
        <YAxis />
        <Tooltip
          formatter={(value) => [`${value} Leads`, "Count"]}
          labelFormatter={(name) => `Date: ${name}`}
        />
        <Legend />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={stroke}
          fill={fill}
          name="Lead Count"
          isAnimationActive={true}
          label={{
            position: "top",
            formatter: (value) => (value > 0 ? value : ""),
            fontSize: 11,
          }}
        />
      </AreaChart>
    </Box>
  );
};
